/**
 * CaseFlow+AI - Bug Report Modal
 * Lightweight bug reporting with recent error logs
 * No Jira, Sentry, or external logging - just simple feedback
 */
import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { AlertTriangle, Bug, CheckSquare, Square, X } from 'lucide-react';
import logger, { type BufferedLogEntry } from '../../lib/logger';
import { useTheme } from '../../theme/ThemeProvider';
import { useTranslation } from '../../i18n/LanguageProvider';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: BugReportData) => void;
}

export interface BugReportData {
  type: 'bug_report';
  description: string;
  category: BugCategory;
  severity: BugSeverity;
  stepsToReproduce?: string;
  current_route: string;
  current_theme: string;
  current_language: string;
  selected_log_id?: string;
  selected_log_message?: string;
  selected_log_stack?: string;
  recent_logs_snapshot?: BufferedLogEntry[];
}

export type BugCategory = 'ui' | 'functionality' | 'performance' | 'data' | 'other';
export type BugSeverity = 'low' | 'medium' | 'high' | 'critical';

const BUG_CATEGORIES: { value: BugCategory; labelSv: string; labelEn: string }[] = [
  { value: 'ui', labelSv: 'Gränssnitt', labelEn: 'UI/UX' },
  { value: 'functionality', labelSv: 'Funktionalitet', labelEn: 'Functionality' },
  { value: 'performance', labelSv: 'Prestanda', labelEn: 'Performance' },
  { value: 'data', labelSv: 'Data/Information', labelEn: 'Data/Information' },
  { value: 'other', labelSv: 'Annat', labelEn: 'Other' },
];

const SEVERITY_OPTIONS: { value: BugSeverity; labelSv: string; labelEn: string; color: string }[] = [
  { value: 'low', labelSv: 'Låg', labelEn: 'Low', color: 'bg-green-100 text-green-800' },
  { value: 'medium', labelSv: 'Medium', labelEn: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'high', labelSv: 'Hög', labelEn: 'High', color: 'bg-orange-100 text-orange-800' },
  { value: 'critical', labelSv: 'Kritisk', labelEn: 'Critical', color: 'bg-red-100 text-red-800' },
];

export function BugReportModal({ isOpen, onClose, onSubmit }: BugReportModalProps) {
  const { theme } = useTheme();
  const { t, language } = useTranslation();

  // Form state
  const [category, setCategory] = useState<BugCategory>('functionality');
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [severity, setSeverity] = useState<BugSeverity>('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Log selection state
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [showLogs, setShowLogs] = useState(true);

  // Get recent logs from logger buffer
  const recentLogs = useMemo(() => {
    return logger.getRecentLogs();
  }, [isOpen]); // Refresh when modal opens

  const selectedLog = useMemo(() => {
    return recentLogs.find(log => log.id === selectedLogId) || null;
  }, [selectedLogId, recentLogs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    setIsSubmitting(true);

    try {
      const reportData: BugReportData = {
        type: 'bug_report',
        description: description.trim(),
        category,
        severity,
        stepsToReproduce: stepsToReproduce.trim() || undefined,
        current_route: window.location.pathname + window.location.search,
        current_theme: theme,
        current_language: language,
        selected_log_id: selectedLog?.id,
        selected_log_message: selectedLog?.message,
        selected_log_stack: selectedLog?.stack,
        recent_logs_snapshot: recentLogs.slice(0, 5).map(log => ({
          ...log,
          maskedContext: log.maskedContext?.slice(0, 2000),
        })),
      };

      logger.audit('Bug report submitted', {
        category,
        severity,
        hasSelectedLog: !!selectedLog,
        route: reportData.current_route,
      });

      onSubmit?.(reportData);

      // Reset form
      setDescription('');
      setStepsToReproduce('');
      setCategory('functionality');
      setSeverity('medium');
      setSelectedLogId(null);
      onClose();
    } catch (error) {
      logger.error('Failed to submit bug report', { error: String(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedLogId(null);
    onClose();
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString(language === 'sv' ? 'sv-SE' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  const getLevelBadge = (level: string) => {
    const colors = {
      error: 'bg-red-100 text-red-800',
      warn: 'bg-yellow-100 text-yellow-800',
    };
    return (
      <Badge className={colors[level as keyof typeof colors] || 'bg-gray-100'}>
        {level === 'error' ? 'Fel' : 'Varning'}
      </Badge>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-red-500" />
            Rapportera bugg
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="bug-category">Kategori *</Label>
            <select
              id="bug-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as BugCategory)}
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#0f0f11]"
              required
            >
              {BUG_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {language === 'sv' ? cat.labelSv : cat.labelEn}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="bug-description">Beskrivning *</Label>
            <Textarea
              id="bug-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={language === 'sv'
                ? 'Beskriv vad som hände...'
                : 'Describe what happened...'}
              className="min-h-[100px]"
              required
            />
          </div>

          {/* Steps to Reproduce */}
          <div className="space-y-2">
            <Label htmlFor="bug-steps">
              {language === 'sv' ? 'Steg för att återskapa' : 'Steps to Reproduce'}
            </Label>
            <Textarea
              id="bug-steps"
              value={stepsToReproduce}
              onChange={(e) => setStepsToReproduce(e.target.value)}
              placeholder={language === 'sv'
                ? '1. Gå till...\n2. Klicka på...\n3. Felet uppstår...'
                : '1. Go to...\n2. Click on...\n3. Error occurs...'}
              className="min-h-[80px]"
            />
          </div>

          {/* Severity */}
          <div className="space-y-2">
            <Label htmlFor="bug-severity">Allvarlighetsgrad</Label>
            <div className="flex gap-2 flex-wrap">
              {SEVERITY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSeverity(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    severity === opt.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5'
                  }`}
                >
                  <span className={`inline-block px-2 py-0.5 rounded ${opt.color} mr-2`}>
                    {language === 'sv' ? opt.labelSv : opt.labelEn}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Recent Error Logs Section */}
          {recentLogs.length > 0 && (
            <div className="space-y-2 border-t pt-4 dark:border-white/10">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  {language === 'sv' ? 'Senaste felmeddelanden' : 'Recent Error Messages'}
                </Label>
                <button
                  type="button"
                  onClick={() => setShowLogs(!showLogs)}
                  className="text-sm text-blue-500 hover:text-blue-600"
                >
                  {showLogs
                    ? (language === 'sv' ? 'Dölj' : 'Hide')
                    : (language === 'sv' ? 'Visa' : 'Show')}
                </button>
              </div>

              {showLogs && (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {recentLogs.map(log => (
                    <div
                      key={log.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedLogId === log.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5'
                      }`}
                      onClick={() => setSelectedLogId(selectedLogId === log.id ? null : log.id)}
                    >
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <div className="mt-0.5">
                          {selectedLogId === log.id
                            ? <CheckSquare className="h-4 w-4 text-blue-500" />
                            : <Square className="h-4 w-4 text-gray-400" />}
                        </div>

                        {/* Log content */}
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {getLevelBadge(log.level)}
                            <span className="text-xs text-gray-500">
                              {formatTimestamp(log.timestamp)}
                            </span>
                            {log.route && (
                              <span className="text-xs text-gray-400">
                                {log.route}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {log.message}
                          </p>
                          {log.component && (
                            <p className="text-xs text-gray-400">
                              {language === 'sv' ? 'Komponent' : 'Component'}: {log.component}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedLogId && (
                <p className="text-xs text-gray-500">
                  {language === 'sv'
                    ? 'Valt fel kommer att kopplas till buggrapporten'
                    : 'Selected error will be linked to the bug report'}
                </p>
              )}
            </div>
          )}

          {/* Footer */}
          <DialogFooter className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <X className="h-4 w-4 mr-2" />
              {language === 'sv' ? 'Avbryt' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              disabled={!description.trim() || isSubmitting}
            >
              <Bug className="h-4 w-4 mr-2" />
              {isSubmitting
                ? (language === 'sv' ? 'Skickar...' : 'Submitting...')
                : (language === 'sv' ? 'Skicka buggrapport' : 'Submit Bug Report')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default BugReportModal;
